import { createFileRoute } from "@tanstack/react-router";
import { requireFeature } from "@/lib/feature-gate";

export const Route = createFileRoute(
  "/user/_layout/inventory/deployment-manifests",
)({
  beforeLoad: () => requireFeature("inventory"),
  component: UserDeploymentManifestsPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { SidebarInset } from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useFetchDeploymentManifests,
  useDeploymentManifestMutations,
} from "@/hooks/api/inventory/useDeploymentManifests";
import { useFetchWarehouses, ALL_ACTIVE_PARAMS } from "@/hooks/api/inventory/useCatalog";
import { DeploymentManifestTable } from "@/components/inventory/tables/DeploymentManifestTable";
import { DeploymentManifestDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import { useAuthStore } from "@/stores/useAuthStore";
import type { DeploymentManifest } from "@/types/inventory";

const DEPLOYMENT_MANIFEST_STATUS_OPTIONS = [
  { label: "Issued", value: "ISSUED" },
  { label: "Acknowledged", value: "ACKNOWLEDGED" },
  { label: "Partially Returned", value: "PARTIALLY_RETURNED" },
  { label: "Closed", value: "CLOSED" },
  { label: "Cancelled", value: "CANCELLED" },
];

// Named to match the admin side's own term ("Deployment Manifests", per
// src/routes/admin/_layout/inventory/deployment-manifests.tsx) rather than
// "Assets" -- a manifest's lines can be ordinary bulk/consumable items, not
// only durable AssetItem-tracked equipment, so "My Assigned Assets" would
// have been inaccurate and easily confused with the separate Asset Items
// section under Assets & Rentals.
//
// Note: this filters by `custodian_user` client-side only (same convention
// as /user/inventory/requisitions' `requested_by` filter today) -- the
// backend endpoint doesn't yet enforce this scoping itself. See
// INVENTORY_ACCESS_CONTROL_PLAN.md Phase 2 for the server-side hardening
// that's intentionally deferred, not done here.
function UserDeploymentManifestsPage() {
  const { user } = useAuthStore();

  const { page, setPage, pageSize, handlePageSizeChange } = usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [viewing, setViewing] = useState<DeploymentManifest | null>(null);

  const manifestsQuery = useFetchDeploymentManifests(
    {
      page,
      page_size: pageSize,
      custodian_user: user?.user_id,
      search: debouncedSearch || undefined,
      status: statusFilter,
      source_warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
    },
    { enabled: user?.user_id != null }
  );
  const manifests = manifestsQuery.data?.results ?? [];
  const count = manifestsQuery.data?.count ?? 0;

  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];

  const { acknowledgeManifest } = useDeploymentManifestMutations();
  const isMutating = acknowledgeManifest.isPending;
  const isLoading = manifestsQuery.isPending || isMutating;
  const isFetching = manifestsQuery.isFetching || isMutating;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, warehouseFilter, setPage]);

  const resetFilters = () => {
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setWarehouseFilter(undefined);
    setPage(1);
  };

  const handleAcknowledge = async (manifest: DeploymentManifest) => {
    try {
      await acknowledgeManifest.mutateAsync({ id: manifest.id });
      toast.success("Manifest acknowledged");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="My Deployment Manifests"
          description="Items and equipment currently issued to you"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          isLoading={isFetching}
        />

        <FiltersPanel show={showFilters} onClear={resetFilters} isLoading={isFetching}>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by manifest number..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-9 pl-8"
            />
          </div>
          <SelectFilter
            placeholder="Status"
            options={DEPLOYMENT_MANIFEST_STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({ label: w.name, value: String(w.id) }))}
            value={warehouseFilter}
            onChange={(v) => setWarehouseFilter(v)}
          />
        </FiltersPanel>

        <DeploymentManifestTable
          manifests={manifests}
          isLoading={isLoading}
          userMode
          onNameClick={(m) => setViewing(m)}
          onView={(m) => setViewing(m)}
          onEdit={() => {}}
          onIssue={() => {}}
          onAcknowledge={handleAcknowledge}
          onPartialReturn={() => {}}
          onReturnInspect={() => {}}
          onCancel={() => {}}
          onDelete={() => {}}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <DeploymentManifestDetailSheet
          isOpen={!!viewing}
          onClose={() => setViewing(null)}
          manifest={viewing}
        />
      </div>
    </SidebarInset>
  );
}
